import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'i18n/use-translations';
import { Button as FlowbiteButton, type FlowbiteDropdownTheme } from 'flowbite-react';
import type { ComponentSize, SelectMenuItem } from './__commons__/selectCommons';
import {
    itemContainerClass,
    contentContainerClass,
    disabledItemClass,
    enabledItemClass,
} from './__commons__/selectCommons';
import { dropdownTheme } from '../../styles/themes/select';
import {
    selectFieldButtonTheme,
    selectFieldGhostButtonTheme,
    selectGhostPlaceholderClass,
} from '../../styles/themes/button';
import { Text } from '../text';
import { IconLoader } from '../iconLoader';
import { Input, type InputProps } from '../input';
import { useContextualNav, type NavEntry } from './useContextualNav';
import { SelectItemContent } from './select-item-content';
import { SelectEmptyGroup } from './select-empty-group';
import { SelectSearchStatus } from './select-search-status';
import { CheckIndicator } from '../check-indicator';
import { useDismissLayer } from '../../hooks/use-dismiss-layer';
import type { header } from './select';
import { getSelectTriggerId } from './select-trigger-id';

/** A custom trigger: the dropdown sets its id, ARIA state and handlers, and keeps its own handlers and disabled. */
type TriggerProps = React.ButtonHTMLAttributes<HTMLElement>;

export interface ContextualDropdownProps {
    id: string;
    value?: string;
    label?: string;
    helper?: string;
    testId?: string;
    disabled?: boolean;
    header?: header;

    /** 'input': the trigger is the search field. 'button': select-style trigger with the search field in the menu. */
    triggerMode?: 'input' | 'button';
    /** Visually-hidden context prefix for the button trigger's name; the visible value stays in the name. */
    ariaLabel?: string;
    searchLabel?: string;
    placeholder?: string;
    triggerDisplay?: React.ReactNode;
    /** Button mode only: replaces the default trigger. The element gets the trigger's id, ARIA state and handlers. */
    renderTrigger?: () => React.ReactElement;
    errorMessage?: string;
    /** Button mode only: error styling without a message. */
    invalid?: boolean;
    size?: ComponentSize;
    /** Button mode: extra classes for the trigger. */
    buttonClassName?: string;
    /** Button mode: 'ghost' is a borderless trigger that reads as a table cell's text; any other value is the field. */
    variant?: string;
    /** Button mode: shows the trigger's chevron. */
    arrow?: boolean;
    /** Button mode: extra classes for the trigger's chevron. */
    arrowClassName?: string;
    /** Button mode: renders a clear control beside the trigger while something is selected. */
    onClear?: () => void;
    clearLabel?: string;

    middleMenuItems: SelectMenuItem[];
    bottomMenuItems: SelectMenuItem[];
    filteredItems: SelectMenuItem[];

    groups: Record<string, SelectMenuItem[]>;
    groupKeys: string[];
    groupBy?: (item: SelectMenuItem) => string;
    renderGroupHeader?: (groupKey: string) => React.ReactNode;
    groupHeaderClassName?: string;
    groupHeaderTextClassName?: string;
    groupHeaderBorderClassName?: string;
    emptyGroupMessage?: string;

    searchValue?: string;
    onSearchChange?: (term: string) => void;

    inputProps?: Partial<InputProps>;

    multiSelect?: boolean;
    onSelectAll?: (items: SelectMenuItem[]) => void;
    allSelected: boolean;
    isSelectAllDisabled: boolean;
    disableSelectOptions?: boolean;

    onItemSelect: (item: SelectMenuItem) => void;
    /** Called after Escape closes the open menu, which keeps the key from reaching handlers around the field. */
    onEscape?: () => void;
    displaySelectedIcon?: boolean;

    middleMenuClassName?: string;
    middleMenuItemsClassName?: string;
    middleMenuStyle?: React.CSSProperties;
    dropdownClassName?: string;
    dropdownStyle?: React.CSSProperties;

    dropdownThemeOverride?: FlowbiteDropdownTheme;
}

export const ContextualDropdown: React.FC<ContextualDropdownProps> = ({
    id,
    value,
    label,
    helper,
    testId,
    disabled = false,
    header,

    triggerMode = 'input',
    ariaLabel,
    searchLabel,
    placeholder,
    triggerDisplay,
    renderTrigger,
    errorMessage,
    invalid = false,
    size = 'sm',
    buttonClassName,
    variant,
    arrow = true,
    arrowClassName,
    onClear,
    clearLabel,

    middleMenuItems,
    bottomMenuItems,
    filteredItems,

    groups,
    groupKeys,
    groupBy,
    renderGroupHeader,
    groupHeaderClassName = '',
    groupHeaderTextClassName = '',
    groupHeaderBorderClassName = '',
    emptyGroupMessage,

    searchValue = '',
    onSearchChange = () => undefined,

    inputProps,

    multiSelect = false,
    onSelectAll,
    allSelected,
    isSelectAllDisabled,
    disableSelectOptions = false,

    onItemSelect,
    onEscape,
    displaySelectedIcon = true,

    middleMenuClassName = '',
    middleMenuItemsClassName = '',
    middleMenuStyle,
    dropdownClassName = '',
    dropdownStyle,

    dropdownThemeOverride,
}) => {
    const { __ } = useTranslations();
    const resolvedEmptyGroupMessage = emptyGroupMessage ?? __('misc.no_results_in_category');
    const noResultsMessage = emptyGroupMessage ?? __('misc.no_results_found');
    const [contextualOpen, setContextualOpen] = useState(false);
    const contextualRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    // Unlike the option rows, the search field must take focus on click.
    const searchWrapRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const resolvedTheme = dropdownThemeOverride ?? dropdownTheme;

    const isButtonTrigger = triggerMode === 'button';

    const [pos, setPos] = useState({ top: 0, left: 0, width: 0, flipped: false });

    const updatePosition = useCallback(() => {
        if (!contextualRef.current) {
            return;
        }
        const inputRect = contextualRef.current.getBoundingClientRect();
        const dropdownHeight = dropdownRef.current?.offsetHeight ?? 0;
        const dropdownWidth = dropdownRef.current?.offsetWidth ?? 0;
        const spaceBelow = window.innerHeight - inputRect.bottom;
        const fitsBelow = dropdownHeight === 0 || spaceBelow >= dropdownHeight;

        setPos({
            top: fitsBelow ? inputRect.bottom : Math.max(0, inputRect.top - dropdownHeight),
            // A menu wider than its trigger (a narrow column header) is pulled back inside the viewport.
            left: Math.max(0, Math.min(inputRect.left, window.innerWidth - dropdownWidth)),
            width: inputRect.width,
            flipped: !fitsBelow,
        });
    }, []);

    useLayoutEffect(() => {
        if (contextualOpen) {
            updatePosition();
        }
    }, [contextualOpen, updatePosition]);

    useEffect(() => {
        if (!contextualOpen) {
            return;
        }

        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        // Recalculate when dropdown height changes (e.g. filtering shrinks the list)
        const dropdown = dropdownRef.current;
        let resizeObserver: ResizeObserver | undefined;
        if (dropdown) {
            resizeObserver = new ResizeObserver(updatePosition);
            resizeObserver.observe(dropdown);
        }

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
            resizeObserver?.disconnect();
        };
    }, [contextualOpen, updatePosition]);

    const hasSelectAll = multiSelect && !!onSelectAll && middleMenuItems.length > 0;

    // Whether the dropdown has anything to render. Without this, focusing the input opens an
    // empty floating box when there are no items, groups, actions, header, or Select All row.
    // Declared group keys count as content because they still render their empty-state message.
    const hasContent =
        !!header ||
        hasSelectAll ||
        filteredItems.length > 0 ||
        bottomMenuItems.length > 0 ||
        (!!groupBy && groupKeys.length > 0);

    // Button mode hosts the search field, so its menu survives zero results. The combobox reports this,
    // not contextualOpen, as expanded so aria-controls never points at an unmounted listbox.
    const menuVisible = contextualOpen && (isButtonTrigger || hasContent);

    const { navEntries, navIndexMap, activeIndex, setActiveIndex, navCount, handleNavEntryClick, activeNavId } =
        useContextualNav({
            isContextual: true,
            filteredItems,
            bottomMenuItems,
            hasSelectAll,
        });

    // Set when the list is about to change, so the first match is picked from the list that results.
    const [seekFirstMatch, setSeekFirstMatch] = useState(false);

    const focusTrigger = useCallback(() => {
        document.getElementById(getSelectTriggerId(id, 'portal'))?.focus();
    }, [id]);

    const openMenu = useCallback(() => {
        // A fresh open starts unfiltered; keeping the previous term would silently hide options.
        onSearchChange('');
        // Button mode lands on the first option so Enter works at once.
        setActiveIndex(-1);
        setSeekFirstMatch(isButtonTrigger);
        setContextualOpen(true);
    }, [onSearchChange, setActiveIndex, isButtonTrigger]);

    // Effect, not autoFocus: during commit the dropdown ref is unattached and the trigger's blur would close the menu.
    useEffect(() => {
        if (contextualOpen && isButtonTrigger) {
            searchInputRef.current?.focus();
        }
    }, [contextualOpen, isButtonTrigger]);

    // Focus stays on the search field, so the option list has to be scrolled by hand.
    useEffect(() => {
        if (contextualOpen && activeIndex >= 0) {
            document.getElementById(`${id}-nav-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
        }
    }, [contextualOpen, activeIndex, id]);

    const handleSelectAll = useCallback(() => {
        if (isSelectAllDisabled) {
            return;
        }
        const shouldSelectAll = !allSelected;
        const updated = middleMenuItems.map((item) => {
            const isDisabled = (disableSelectOptions && !item.isSelected) || item.isDisabled;
            if (isDisabled) {
                return item;
            }
            return { ...item, isSelected: shouldSelectAll };
        });
        onSelectAll?.(updated);
    }, [isSelectAllDisabled, allSelected, disableSelectOptions, middleMenuItems, onSelectAll]);

    const isOptionDisabled = useCallback(
        (item: SelectMenuItem) => (disableSelectOptions && !item.isSelected) || !!item.isDisabled,
        [disableSelectOptions],
    );

    // Footer actions are always enabled; disabled options and a disabled Select All are skipped by the keyboard.
    const isEntryEnabled = useCallback(
        (entry: NavEntry) =>
            entry.type === 'bottom' || (entry.type === 'item' ? !isOptionDisabled(entry.item) : !isSelectAllDisabled),
        [isOptionDisabled, isSelectAllDisabled],
    );

    // Button mode lands on the first enabled match once the list reflects the term, never Select All or a footer action.
    useEffect(() => {
        if (seekFirstMatch) {
            setSeekFirstMatch(false);
            setActiveIndex(navEntries.findIndex((entry) => entry.type === 'item' && isEntryEnabled(entry)));
        }
    }, [seekFirstMatch, navEntries, setActiveIndex, isEntryEnabled]);

    /** The next enabled entry from `from` moving by `delta`, wrapping, or `from` when none is enabled. */
    const findEnabledEntry = (from: number, delta: number): number => {
        const start = from < 0 ? (delta > 0 ? -1 : navCount) : from;
        for (let offset = 1; offset <= navCount; offset += 1) {
            const index = (((start + offset * delta) % navCount) + navCount) % navCount;
            if (isEntryEnabled(navEntries[index])) {
                return index;
            }
        }
        return from;
    };

    const handleItemClick = useCallback(
        (item: SelectMenuItem) => {
            if (isOptionDisabled(item)) {
                return;
            }
            onItemSelect({ ...item, isSelected: !item.isSelected });
            if (item.onClick) {
                item.onClick();
            }
            if (!multiSelect) {
                // The trigger shows `value`, so the term resets rather than pre-filtering the next open.
                onSearchChange(isButtonTrigger ? '' : item.value);
                setContextualOpen(false);
                if (isButtonTrigger) {
                    focusTrigger();
                }
            }
        },
        [isOptionDisabled, onItemSelect, multiSelect, onSearchChange, isButtonTrigger, focusTrigger],
    );

    // Button mode closes first, so a footer action that opens a dialog hands focus back to the trigger, not body.
    const handleBottomItemClick = useCallback(
        (item: SelectMenuItem) => {
            if (isButtonTrigger) {
                onSearchChange('');
                setContextualOpen(false);
                setActiveIndex(-1);
                focusTrigger();
            }
            item.onClick?.();
        },
        [isButtonTrigger, onSearchChange, setActiveIndex, focusTrigger],
    );

    const groupHeaderId = (index: number) => `${id}-group-${index}`;

    // Presentational inside its role="group", which it names.
    const renderGroupHeaders = (index: number) => (
        <div
            role="presentation"
            className={
                index === 0
                    ? ''
                    : `border-solid border-t border-b-0 border-l-0 border-r-0 border-system-outline-variant ${groupHeaderBorderClassName}`
            }
        >
            <div className={`px-3 py-1 bg-system-surface ${groupHeaderClassName}`}>
                <Text
                    id={groupHeaderId(index)}
                    variant="title-medium"
                    className={`cursor-default ${groupHeaderTextClassName}`}
                >
                    {renderGroupHeader ? renderGroupHeader(groupKeys[index]) : groupKeys[index]}
                </Text>
            </div>
        </div>
    );

    const handleOuterBlur = (e: React.FocusEvent) => {
        const related = e.relatedTarget as Node;
        if (!contextualRef.current?.contains(related) && !dropdownRef.current?.contains(related)) {
            setContextualOpen(false);
            setActiveIndex(-1);
        }
    };

    // Escape closes the open menu through the shared stack, before any enclosing Drawer or Modal sees it.
    useDismissLayer({
        enabled: contextualOpen,
        onDismiss: () => {
            setContextualOpen(false);
            setActiveIndex(-1);
            if (isButtonTrigger) {
                focusTrigger();
            }
            onEscape?.();
        },
        getElements: () => [contextualRef.current, dropdownRef.current],
    });

    // A closed menu, or a key it does not handle, is left to enclosing handlers.
    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!contextualOpen) {
            return;
        }
        let handled = true;
        switch (e.key) {
            case 'ArrowDown':
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(findEnabledEntry(activeIndex, e.key === 'ArrowDown' ? 1 : -1));
                break;
            case 'Enter': {
                const entry = navEntries[activeIndex];
                if (!entry) {
                    handled = false;
                    break;
                }
                e.preventDefault();
                handleNavEntryClick(activeIndex, handleSelectAll, handleItemClick, handleBottomItemClick);
                // Multi-select keeps the toggled option active; a disabled option keeps the keyboard position
                if (!multiSelect && !(entry.type === 'item' && isOptionDisabled(entry.item))) {
                    setActiveIndex(-1);
                }
                break;
            }
            // The menu is portalled to body, so button mode refocuses the trigger before the default Tab move;
            // Shift+Tab is swallowed since its default move would skip the refocused trigger.
            case 'Tab':
                if (isButtonTrigger && e.shiftKey) {
                    e.preventDefault();
                }
                setContextualOpen(false);
                setActiveIndex(-1);
                if (isButtonTrigger) {
                    focusTrigger();
                }
                break;
            case 'Delete': {
                const entry = navEntries[activeIndex];
                if (entry && entry.type === 'item' && entry.item.onAction) {
                    e.preventDefault();
                    entry.item.onAction(entry.item);
                } else {
                    handled = false;
                }
                break;
            }
            default:
                handled = false;
                break;
        }
        if (handled) {
            e.stopPropagation();
        }
    };

    const searchInput = (
        <Input
            {...inputProps}
            // Inline mode: the search field is the labelled control, so it takes `id`.
            // Button mode: the trigger owns `<id>-trigger`, so the field only gets an id passed via inputProps.
            id={isButtonTrigger ? inputProps?.id : id}
            type="text"
            placeholder={
                inputProps?.placeholder ?? (isButtonTrigger ? searchLabel : value) ?? __('misc.search_placeholder')
            }
            value={searchValue}
            onChange={(e) => {
                onSearchChange(e.currentTarget.value);
                // Button mode keeps the first match active so Enter selects it without arrowing.
                setActiveIndex(-1);
                setSeekFirstMatch(isButtonTrigger);
                if (!contextualOpen) {
                    setContextualOpen(true);
                }
            }}
            onKeyDown={handleSearchKeyDown}
            onFocus={() => {
                if (!isButtonTrigger) {
                    setContextualOpen(true);
                    setActiveIndex(-1);
                }
            }}
            labelValue={isButtonTrigger ? undefined : inputProps?.labelValue ?? label}
            helperText={isButtonTrigger ? undefined : inputProps?.helperText ?? helper}
            ariaLabel={
                inputProps?.ariaLabel ??
                (isButtonTrigger ? searchLabel ?? __('misc.search_placeholder') : ariaLabel ?? label)
            }
            testId={
                isButtonTrigger
                    ? inputProps?.testId ?? (testId ? `${testId}-search` : undefined)
                    : inputProps?.testId ?? testId
            }
            disabled={inputProps?.disabled ?? disabled}
            // No Clear button: Tab closes the menu, so it would be an unreachable tab stop.
            clearable={inputProps?.clearable ?? !isButtonTrigger}
            rightIconLabel={inputProps?.rightIconLabel ?? __('misc.clear')}
            leftIcon={isButtonTrigger ? 'IconMagnifier' : inputProps?.leftIcon}
            inputRef={isButtonTrigger ? searchInputRef : inputProps?.inputRef}
            role="combobox"
            aria-expanded={menuVisible}
            aria-controls={menuVisible ? `${id}-listbox` : undefined}
            aria-activedescendant={menuVisible ? activeNavId(id) : undefined}
            aria-autocomplete="list"
        />
    );

    const triggerContent = triggerDisplay ?? value;
    // After a pick the field holds the chosen value, which is not a search worth announcing on refocus.
    const searchIsSelection =
        searchValue !== '' &&
        (searchValue === value || middleMenuItems.some((item) => item.isSelected && item.value === searchValue));
    // ariaLabel prefixes the button trigger's name, so the popup takes the same prefix plus the field's label.
    const popupName = isButtonTrigger
        ? [ariaLabel, label].filter(Boolean).join(' ') || value
        : ariaLabel ?? label ?? value;
    const showClear = isButtonTrigger && !!onClear && middleMenuItems.some((i) => i.isSelected);

    const toggleMenu = () => (contextualOpen ? setContextualOpen(false) : openMenu());

    const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
        if (!contextualOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            openMenu();
        }
    };

    const triggerInvalid = !!errorMessage || invalid;
    const triggerId = getSelectTriggerId(id, 'portal');
    const isGhost = variant === 'ghost';
    // Shared by the default and custom triggers; unset ARIA is left out so it never clobbers a custom trigger's own.
    const triggerProps = {
        id: triggerId,
        // "dialog": the popup is a search field plus a listbox, controlled by the search combobox.
        'aria-haspopup': 'dialog' as const,
        'aria-expanded': contextualOpen,
        ...(triggerInvalid ? { 'aria-invalid': true } : {}),
        ...(errorMessage ? { 'aria-describedby': `${id}-error` } : {}),
        // A visible label is wired via aria-labelledby; the default trigger renders the hidden-context case as text.
        ...(!ariaLabel && label ? { 'aria-labelledby': `${id}-label ${triggerId}` } : {}),
        className: `${buttonClassName ?? ''} ${triggerInvalid ? '!border-system-error' : ''}`,
    };

    const customTrigger = renderTrigger?.() as React.ReactElement<TriggerProps> | undefined;

    // Keeps the trigger's own handlers, classes and disabled state alongside the dropdown's.
    const triggerButton = customTrigger ? (
        React.cloneElement(customTrigger, {
            ...triggerProps,
            // A custom trigger's layout is unknown, so it reserves the clear control's space with padding.
            className: `${customTrigger.props.className ?? ''} ${triggerProps.className} ${showClear ? 'pr-14' : ''}`,
            disabled: customTrigger.props.disabled || disabled,
            onClick: (event: React.MouseEvent<HTMLElement>) => {
                customTrigger.props.onClick?.(event);
                toggleMenu();
            },
            onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
                customTrigger.props.onKeyDown?.(event);
                handleTriggerKeyDown(event);
            },
        })
    ) : (
        <FlowbiteButton
            {...triggerProps}
            data-testid={testId}
            size={size}
            color="primary"
            theme={isGhost ? selectFieldGhostButtonTheme : selectFieldButtonTheme}
            disabled={disabled}
            onClick={toggleMenu}
            onKeyDown={handleTriggerKeyDown}
        >
            {/* Hidden text rather than aria-label so the visible value stays in the accessible name. */}
            {ariaLabel && <span className="sr-only">{ariaLabel}</span>}
            {/* body-medium (14px) matches the Input's text-sm so the two land on the same height; pr-6 makes room for the clear control. */}
            <Text
                variant="body-medium"
                className={`flex-1 text-left truncate min-h-[1lh] ${
                    triggerContent ? '' : isGhost ? selectGhostPlaceholderClass : 'text-system-on-surface-variant'
                } ${showClear ? 'pr-6' : ''}`}
            >
                {triggerContent || placeholder}
            </Text>
            {arrow && (
                <IconLoader
                    className={`stroke-system-on-surface-variant fill-system-on-surface-variant ${
                        arrowClassName ?? ''
                    }`}
                    size="sm"
                    iconName={contextualOpen ? 'IconChevronUp' : 'IconChevronDown'}
                />
            )}
        </FlowbiteButton>
    );

    const renderOption = (item: SelectMenuItem) => {
        const itemDisabled = isOptionDisabled(item);
        const navIdx = navIndexMap.get(item) ?? -1;
        return (
            <div
                key={item.id}
                id={`${id}-nav-${navIdx}`}
                role="option"
                aria-selected={item.isSelected}
                aria-disabled={itemDisabled || undefined}
                aria-keyshortcuts={item.onAction ? 'Delete' : undefined}
                className={`${resolvedTheme.floating.item.base} ${middleMenuItemsClassName} ${
                    itemDisabled ? disabledItemClass : enabledItemClass
                } ${item.variant === 'destruct' ? 'text-system-error' : ''} ${
                    activeIndex === navIdx ? 'bg-system-surface-container' : ''
                }`}
                onClick={() => handleItemClick(item)}
                onMouseEnter={() => setActiveIndex(navIdx)}
                data-testid={item.testId}
            >
                <SelectItemContent
                    item={item}
                    multiSelect={multiSelect}
                    displaySelectedIcon={displaySelectedIcon}
                    enableActions
                />
            </div>
        );
    };

    return (
        // Button mode fills its container (a table cell, typically) so sibling triggers line up.
        <div
            className={`flex flex-col gap-1 relative ${isButtonTrigger ? 'w-full' : ''}`}
            ref={contextualRef}
            onBlur={handleOuterBlur}
        >
            {isButtonTrigger && label && (
                <Text id={`${id}-label`} variant="label-large">
                    {label}
                </Text>
            )}
            {isButtonTrigger ? (
                // The clear control sits over the trigger as a sibling, since a button cannot nest another.
                <div className="relative">
                    {triggerButton}
                    {/* Matches the z-index 1 hover-state gives the trigger, and wins by coming later in the DOM. */}
                    {showClear && (
                        <button
                            type="button"
                            aria-label={clearLabel ?? __('misc.clear')}
                            disabled={disabled}
                            onClick={() => {
                                onClear?.();
                                setContextualOpen(false);
                                setActiveIndex(-1);
                                focusTrigger();
                            }}
                            className="absolute z-[1] right-9 top-1/2 -translate-y-1/2 flex items-center p-0 border-none bg-transparent cursor-pointer rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-primary"
                        >
                            <IconLoader size="sm" iconName="IconRemove" className="stroke-system-on-surface-variant" />
                        </button>
                    )}
                </div>
            ) : (
                searchInput
            )}
            {/* Input mode: outside the portal so it stays mounted while the menu has nothing to show. */}
            {!isButtonTrigger && (
                <SelectSearchStatus
                    searchValue={contextualOpen && !searchIsSelection ? searchValue : ''}
                    resultCount={filteredItems.length}
                />
            )}
            {isButtonTrigger &&
                (errorMessage ? (
                    <span id={`${id}-error`} role="alert">
                        <Text variant="body-small" className="text-system-error">
                            {errorMessage}
                        </Text>
                    </span>
                ) : (
                    helper && (
                        <Text variant="label-large" className="text-system-on-surface-variant">
                            {helper}
                        </Text>
                    )
                ))}
            {menuVisible &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        // Matches the trigger's aria-haspopup="dialog".
                        role={isButtonTrigger ? 'dialog' : undefined}
                        aria-label={isButtonTrigger ? popupName : undefined}
                        // Above the ui Drawer (z-index 9998), so a Select inside one opens on top of it.
                        className={`z-[9999] py-1 ${resolvedTheme.floating.style.auto} ${
                            pos.flipped ? 'mb-1 mt-0' : ''
                        } ${dropdownClassName}`}
                        style={{
                            position: 'fixed',
                            top: pos.top,
                            left: pos.left,
                            width: pos.width,
                            ...dropdownStyle,
                        }}
                        onBlur={handleOuterBlur}
                        onMouseDown={(e) => {
                            if (!searchWrapRef.current?.contains(e.target as Node)) {
                                e.preventDefault();
                            }
                        }}
                    >
                        {isButtonTrigger && (
                            <>
                                <div ref={searchWrapRef} className="px-3 py-2 bg-system-surface">
                                    {searchInput}
                                </div>
                                <div className="border-solid border-b border-t-0 border-l-0 border-r-0 border-system-outline-variant" />
                            </>
                        )}
                        {/* Always mounted: a live region inserted with its content is often not announced. */}
                        {isButtonTrigger && (
                            <SelectSearchStatus
                                searchValue={searchValue}
                                resultCount={filteredItems.length}
                                // Declared groups show their own empty message.
                                visibleEmptyMessage={!groupBy || groupKeys.length === 0 ? noResultsMessage : undefined}
                            />
                        )}
                        {/* Outside the listbox, which may only own options and groups. */}
                        {header && (
                            <div className={resolvedTheme.floating.header}>
                                <Text variant="title-large">{header.title}</Text>
                                {header.subtitle && <Text variant="label-large">{header.subtitle}</Text>}
                            </div>
                        )}
                        <div
                            id={`${id}-listbox`}
                            role="listbox"
                            aria-label={popupName}
                            aria-multiselectable={multiSelect || undefined}
                        >
                            <div role="none" className={`${middleMenuClassName} min-h-0`} style={middleMenuStyle}>
                                {hasSelectAll && (
                                    <>
                                        <div
                                            id={`${id}-nav-0`}
                                            role="option"
                                            aria-selected={allSelected}
                                            aria-disabled={isSelectAllDisabled || undefined}
                                            className={`${
                                                resolvedTheme.floating.item.base
                                            } ${middleMenuItemsClassName} ${
                                                isSelectAllDisabled ? disabledItemClass : ''
                                            } ${activeIndex === 0 ? 'bg-system-surface-container' : ''}`}
                                            onClick={handleSelectAll}
                                            onMouseEnter={() => setActiveIndex(0)}
                                        >
                                            <div className={itemContainerClass}>
                                                {/* Decorative: the row carries the checked state. */}
                                                <CheckIndicator
                                                    id={`${id}-select-all`}
                                                    checked={allSelected}
                                                    className="mr-2"
                                                />
                                                <div className={contentContainerClass}>
                                                    <Text variant="label-large">{__('misc.select_all')}</Text>
                                                </div>
                                            </div>
                                        </div>
                                        <div role="none" className={resolvedTheme.floating.divider} />
                                    </>
                                )}
                                {groupKeys.map((key, index) => {
                                    const groupItems = groups[key] ?? [];
                                    if (!groupBy) {
                                        return (
                                            <React.Fragment key={key}>{groupItems.map(renderOption)}</React.Fragment>
                                        );
                                    }
                                    const emptyId = `${groupHeaderId(index)}-empty`;
                                    return (
                                        <div
                                            key={key}
                                            role="group"
                                            aria-labelledby={groupHeaderId(index)}
                                            aria-describedby={groupItems.length === 0 ? emptyId : undefined}
                                        >
                                            {renderGroupHeaders(index)}
                                            {groupItems.length === 0 && (
                                                <SelectEmptyGroup id={emptyId} message={resolvedEmptyGroupMessage} />
                                            )}
                                            {groupItems.map(renderOption)}
                                        </div>
                                    );
                                })}
                            </div>
                            {bottomMenuItems.length > 0 && (
                                <>
                                    <div role="none" className={resolvedTheme.floating.divider} />
                                    {bottomMenuItems.map((item) => {
                                        const navIdx = navIndexMap.get(item) ?? -1;
                                        const Icon = item.icon;
                                        return (
                                            <div
                                                key={item.id}
                                                id={`${id}-nav-${navIdx}`}
                                                role="option"
                                                aria-selected={false}
                                                className={`${
                                                    resolvedTheme.floating.item.base
                                                } cursor-pointer text-system-on-surface ${
                                                    activeIndex === navIdx ? 'bg-system-surface-container' : ''
                                                }`}
                                                onClick={() => handleBottomItemClick(item)}
                                                onMouseEnter={() => setActiveIndex(navIdx)}
                                            >
                                                {item.iconName ? (
                                                    <IconLoader
                                                        size="sm"
                                                        iconName={item.iconName}
                                                        className={resolvedTheme.floating.item.icon}
                                                    />
                                                ) : (
                                                    Icon && (
                                                        <Icon
                                                            className={resolvedTheme.floating.item.icon}
                                                            aria-hidden="true"
                                                        />
                                                    )
                                                )}
                                                <Text
                                                    variant="title-small"
                                                    className={item.iconName || Icon ? '' : 'ml-2'}
                                                >
                                                    {item.value}
                                                </Text>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};
